import React, { useLayoutEffect, useRef, useState } from "react";
import { SoonBadge, SOON_TOOLTIP } from "../../atoms/SoonBadge";
import { Tooltip } from "../../atoms/Tooltip";
import {
  OptionButton,
  OptionDivider,
  OptionIcon,
  OptionLabel,
  OptionTrailing,
  SoonMark,
  Shell,
  Thumb,
} from "./SegmentedPicker.styles";
import type { SegmentedPickerProps } from "./SegmentedPicker.types";

/**
 * Figma 3728:484453 "Modes" / SegmentedPicker — text options in a bordered shell.
 * Stateless: selection is lifted via `value` / `onChange`.
 */
export const SegmentedPicker: React.FC<SegmentedPickerProps> = ({
  options,
  value,
  onChange,
  ariaLabel = "Options",
  variant = "compact",
  size = "default",
  fullWidth = false,
  tone = "default",
  slidingThumb = false,
  className,
}) => {
  const thumbOn = slidingThumb && (variant === "pill" || variant === "homeModes");
  const shellRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  // Once the first position is set, later moves animate; the first never does.
  const [thumbReady, setThumbReady] = useState(false);
  useLayoutEffect(() => {
    if (!thumbOn) return undefined;
    const measure = () => {
      const shell = shellRef.current;
      const active = shell?.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]');
      if (!shell || !active) {
        setThumb(null);
        return;
      }
      const a = active.getBoundingClientRect();
      const s = shell.getBoundingClientRect();
      setThumb({ x: a.left - s.left - shell.clientLeft, y: a.top - s.top - shell.clientTop, w: a.width, h: a.height });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [thumbOn, value, options, size]);
  useLayoutEffect(() => {
    if (thumb && !thumbReady) {
      const frame = requestAnimationFrame(() => setThumbReady(true));
      return () => cancelAnimationFrame(frame);
    }
    return undefined;
  }, [thumb, thumbReady]);
  // The `form` variant is built for the full-width, text-labelled delivery
  // condition picker. An icon-only `form` group — Chart Studio's legend
  // position — has no label to fill that width with, so it shrinks to its
  // glyphs instead of stretching the border across the panel.
  const iconOnly = variant === "form" && options.every((option) => !option.label);

  // Arrow keys move the selection to the next option that can be chosen (disabled and soon ones are skipped).
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step) return;
    const enabled = options.filter((option) => !option.disabled && !option.soon);
    if (enabled.length === 0) return;
    event.preventDefault();
    const at = enabled.findIndex((option) => option.id === value);
    // No enabled option selected yet: forward starts at the first, backward at the last.
    const nextIndex = at === -1 ? (step > 0 ? 0 : enabled.length - 1) : (at + step + enabled.length) % enabled.length;
    const next = enabled[nextIndex];
    onChange(next.id);
    const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    buttons[options.indexOf(next)]?.focus();
  };

  return (
    <Shell
      ref={shellRef}
      onKeyDown={handleKeyDown}
      className={className}
      role="radiogroup"
      aria-label={ariaLabel}
      data-variant={variant}
      data-testid="segmented-picker"
      $variant={variant}
      $small={size === "sm"}
      $fullWidth={fullWidth}
      $iconOnly={iconOnly}
      $onDark={tone === "onDark"}
      $thumb={thumbOn}
    >
      {thumbOn && thumb ? (
        <Thumb
          aria-hidden="true"
          data-testid="segmented-picker-thumb"
          $variant={variant}
          $ready={thumbReady}
          style={{ width: thumb.w, height: thumb.h, transform: `translate(${thumb.x}px, ${thumb.y}px)` }}
        />
      ) : null}
      {options.map((option, index) => {
        const active = option.id === value;
        const disabled = Boolean(option.disabled || option.soon);
        const previousOption = options[index - 1];
        const showDivider =
          variant === "chartStudio"
            ? index > 0 && previousOption.id !== value && !active
            : Boolean(option.dividerBefore);

        const button = (
          <OptionButton
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={option.ariaLabel}
            aria-disabled={disabled || undefined}
            disabled={disabled}
            $active={active}
            $variant={variant}
            $small={size === "sm"}
            $fullWidth={fullWidth}
            $onDark={tone === "onDark"}
            $optionId={option.id}
            $iconOnly={iconOnly}
            $thumb={thumbOn}
            className="no-drag"
            onClick={() => onChange(option.id)}
          >
            {option.icon ? (
              <OptionIcon aria-hidden="true">{option.icon}</OptionIcon>
            ) : null}
            <OptionLabel $active={active} $variant={variant}>
              {option.label}
            </OptionLabel>
            {option.soon ? (
              <SoonMark>
                <SoonBadge />
              </SoonMark>
            ) : null}
            {/* INTEGRATION-CHECK: sr/home `endAdornment` and sr/sql `trailing` overlap; both kept, fold into one later. */}
            {option.endAdornment ?? null}
            {option.trailing ? <OptionTrailing>{option.trailing}</OptionTrailing> : null}
          </OptionButton>
        );

        // Icon-only options (empty `label`) carry their whole meaning in the
        // glyph, so `ariaLabel` — otherwise screen-reader-only — is surfaced as
        // a visible hover tooltip too. Labeled options already read on sight.
        const showsTooltip = !option.label && Boolean(option.ariaLabel);

        return (
          <React.Fragment key={option.id}>
            {showDivider ? (
              <OptionDivider
                aria-hidden="true"
                data-testid="segmented-picker-divider"
              />
            ) : null}
            {option.soon ? (
              <Tooltip label={SOON_TOOLTIP} position="top">
                {button}
              </Tooltip>
            ) : showsTooltip ? (
              <Tooltip label={option.ariaLabel as string} position="top">
                {button}
              </Tooltip>
            ) : (
              button
            )}
          </React.Fragment>
        );
      })}
    </Shell>
  );
};

export default SegmentedPicker;
